import React, { createContext, type FC, useContext, useMemo } from "react";
import { useGoogleSignInSetup } from "@components/GoogleIdentity/useGoogleSignInSetup";
import { useGoogleClientId } from "@edreamsodigeo/frontend-core";

import { hideGoogleOneTap } from "./actions";
import type { GoogleAuthActions, GoogleIdentityContextType } from "./contract";
import { TrackingErrorComponent } from "./GoogleTrackingErrorProvider/TrackingErrorComponent";

const GoogleIdentityContext = createContext<
  GoogleIdentityContextType | undefined
>(undefined);
GoogleIdentityContext.displayName = "GoogleIdentityContext";

const GoogleIdentityProvider: FC = ({ children }) => {
  const googleKey = useGoogleClientId();

  const {
    isGoogleScriptLoaded,
    googleLoginSuccessToken,
    state,
    resetGoogleSignIn,
  } = useGoogleSignInSetup({
    appId: googleKey,
  });

  const value: GoogleIdentityContextType = useMemo(
    () => ({
      actions: { hideGoogleOneTap },
      isGoogleScriptLoaded,
      googleLoginSuccessToken,
      resetGoogleSignIn,
      state,
    }),
    [isGoogleScriptLoaded, googleLoginSuccessToken, state, resetGoogleSignIn],
  );

  return (
    <GoogleIdentityContext.Provider value={value}>
      {children}
      <TrackingErrorComponent />
    </GoogleIdentityContext.Provider>
  );
};

export const useGoogleSignInStatus = (): GoogleIdentityContextType => {
  const context = useContext(GoogleIdentityContext);
  if (context === undefined) {
    throw new Error(
      "useGoogleSignInStatus must be used within a GoogleIdentityProvider",
    );
  }
  return context;
};

export const useGoogleAuthActions = (): Partial<GoogleAuthActions> => {
  const context = useContext(GoogleIdentityContext);
  if (context === undefined) {
    throw new Error(
      "useGoogleAuthActions must be used within a GoogleIdentityProvider",
    );
  }
  return context.actions;
};

export default GoogleIdentityProvider;
